import { Customer } from './customers';
import { CustomerVehicle } from './customerVehicles';
import type { ProductVariant } from './productVariants';
import { apiFetch } from '../utils/api';

export type AppointmentStatus =
  | 'scheduled'
  | 'confirmed'
  | 'completed'
  | 'cancelled'
  | 'no_show';

export interface Appointment {
  id: string;
  customer: Customer | null;
  customerId: string;
  locationId: string;
  vehicleId: string | null;
  vehicle?: CustomerVehicle | null;
   location?: {
    id: string;
    name: string;
    code: string | null;
  } | null;
  advisorId: string | null;
  orderId?: string | null;
  status: AppointmentStatus;
  scheduledAt: string;
  source: 'phone' | 'web_link';
  notes: string | null;
  services?: ProductVariant[];
  createdAt: string;
  updatedAt: string;
}

export interface FetchAppointmentsParams {
  page?: number;
  limit?: number;
  search?: string;
  from?: string;
  to?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    totalPages: number;
  };
}

export interface CreateAppointmentPayload {
  customerId: string;
  vehicleId: string;
  locationId: string;
  advisorId?: string | null;
  serviceIds?: string[];
  scheduledAt: string;
  notes?: string | null;
}

export async function fetchAppointments(
  params: FetchAppointmentsParams,
): Promise<PaginatedResponse<Appointment>> {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.limit) search.set('limit', String(params.limit));
  if (params.search) search.set('search', params.search);
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);

  const query = search.toString();
  return apiFetch<PaginatedResponse<Appointment>>(
    `/appointments${query ? `?${query}` : ''}`,
  );
}

export async function createAppointment(
  payload: CreateAppointmentPayload,
): Promise<Appointment> {
  return apiFetch<Appointment>('/appointments', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchAppointment(id: string): Promise<Appointment> {
  return apiFetch<Appointment>(`/appointments/${id}`);
}

export interface UpdateAppointmentPayload {
  scheduledAt?: string;
  status?: AppointmentStatus;
  notes?: string | null;
  serviceIds?: string[];
}

export async function updateAppointment(
  id: string,
  payload: UpdateAppointmentPayload,
): Promise<Appointment> {
  return apiFetch<Appointment>(`/appointments/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function convertAppointmentToOrder(
  id: string,
): Promise<{ orderId: string; alreadyExists?: boolean }> {
  return apiFetch<{ orderId: string; alreadyExists?: boolean }>(
    `/appointments/${id}/convert-to-order`,
    { method: 'POST' },
  );
}

export interface PaginatedCustomerAppointmentsResponse {
  data: Appointment[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const fetchCustomerAppointments = (
  customerId: string,
  page = 1,
  limit = 10,
) =>
  apiFetch<PaginatedCustomerAppointmentsResponse>(
    `/appointments/by-customer/${customerId}?page=${page}&limit=${limit}`,
  );
